import { Link, useLocation } from "react-router";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media-query";

type Voltar = { href: string; rotulo: string };

/** Estado de navegação que um cartão manda junto (`<Link state>`): a página aberta volta pra ele. */
export const voltarPara = (href: string, rotulo: string) => ({ voltarPara: { href, rotulo } satisfies Voltar });

/** O que os cartões do Menu mandam. */
export const VEIO_DO_MENU = voltarPara("/menu", "Menu");

/** Rotas que desenham a tela inteira e põem o botão dentro delas mesmas, não no layout. */
export const ROTAS_COM_VOLTAR_PROPRIO = ["/agenda"];

function useVoltarPara(): Voltar | null {
  const { state, pathname } = useLocation();
  const v = (state as { voltarPara?: Voltar } | null)?.voltarPara;
  return v && v.href !== pathname ? v : null;
}

export const useVeioDoMenu = () => !!useVoltarPara();

/**
 * Página aberta por um cartão (do Menu, ou de um hub como Minha clínica). Mora no estado da
 * navegação: abrir a mesma página pelo menu lateral, por link ou seguindo pra outra tela a partir
 * dela não traz o estado, e some sozinho — sem nenhuma página precisar saber disso.
 *
 * Só no desktop. No celular quem volta é a aba Menu da barra de baixo (que fica marcada), e o
 * topo do conteúdo mostra o nome da página — ver `mobile/TituloDaPagina`.
 */
export function VoltarAoMenu({ className }: { className?: string }) {
  const voltar = useVoltarPara();
  const isMobile = useIsMobile();
  if (!voltar || isMobile) return null;

  return (
    <Link
      to={voltar.href}
      className={cn(
        "inline-flex items-center gap-1.5 text-sm text-zinc-500 transition-colors hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" />
      {voltar.rotulo}
    </Link>
  );
}
